// @ts-strict-ignore
import React from 'react';
import Plugin from 'ckeditor5/packages/ckeditor5-core/src/plugin';
import { toWidget, viewToModelPositionOutsideModelElement } from 'ckeditor5/packages/ckeditor5-widget/src/utils';
import Command from 'ckeditor5/packages/ckeditor5-core/src/command';
import { elementWithRoot } from '../CKEditorPlugins.utilities';
import { EditAssetSelectionLabel } from '@/hybrid/annotation/ckEditorPlugins/components/EditAssetSelectionLabel.molecule';
import { BasePluginDependencies } from '../CkEditorPlugins.module';
import { PluginDependencies } from '@/hybrid/annotation/ckEditorPlugins/plugins/PluginDependencies';
import _ from 'lodash';
import ReactDOM from 'react-dom';
import { ASSET_SELECTION_LABEL_ATTRIBUTES } from '@/hybrid/annotation/ckEditorPlugins/CKEditorPlugins.constants';

const SCHEMA_MODEL_NAME = 'asset_label';
const CLASSES = 'customCkComponent dateRangeLabel';
const DATA_ASSET_SELECTION_ID = ASSET_SELECTION_LABEL_ATTRIBUTES.ASSET_SELECTION_ID;
const DATA_ASSET_SELECTION_DEPTH_LEVEL = ASSET_SELECTION_LABEL_ATTRIBUTES.ASSET_SELECTION_DEPTH_LEVEL;

/**
 * Plugin that enables the use of AssetSelectionLabel components inside the CKEditor.
 * This plugin adds the asset_label command to CKEditor
 */
export class AssetSelectionLabelPlugin extends Plugin {
  private reactDomElements: HTMLElement[] = [];

  init() {
    this.defineSchema();
    this.defineConverters();

    // add command
    this.editor.commands.add('asset_label', new AssetLabelCommand(this.editor));
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass('assetSelectionLabel'),
      ),
    );
  }

  destroy() {
    _.forEach(this.reactDomElements, ReactDOM.unmountComponentAtNode);
  }

  defineSchema() {
    const schema = this.editor.model.schema;
    schema.register(SCHEMA_MODEL_NAME, {
      allowWhere: '$text',
      isInline: true,
      isObject: true,
      allowAttributesOf: '$text',
      allowAttributes: [DATA_ASSET_SELECTION_ID, DATA_ASSET_SELECTION_DEPTH_LEVEL],
    });
  }

  defineConverters() {
    const conversion = this.editor.conversion;

    // converters ((data) view → model)
    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        attributes: {
          [DATA_ASSET_SELECTION_ID]: true,
          [DATA_ASSET_SELECTION_DEPTH_LEVEL]: true,
          class: CLASSES,
        },
      },
      model: (viewElement, { writer: modelWriter }) => {
        return modelWriter.createElement(SCHEMA_MODEL_NAME, {
          [DATA_ASSET_SELECTION_ID]: viewElement.getAttribute(DATA_ASSET_SELECTION_ID),
          [DATA_ASSET_SELECTION_DEPTH_LEVEL]: viewElement.getAttribute(DATA_ASSET_SELECTION_DEPTH_LEVEL),
        });
      },
      converterPriority: 'highest',
    });

    // converters (model → data view)
    conversion.for('dataDowncast').elementToElement({
      model: SCHEMA_MODEL_NAME,
      view: (modelItem, { writer: viewWriter }) => this.createDataElement(modelItem, viewWriter),
    });

    // converters (model → editing view)
    conversion.for('editingDowncast').elementToElement({
      model: SCHEMA_MODEL_NAME,
      view: (modelItem, { writer: viewWriter }) => {
        const widgetElement = this.createViewElement(modelItem, viewWriter);

        // Enable widget handling on a placeholder element inside the editing view.
        return toWidget(widgetElement, viewWriter);
      },
    });
    conversion.for('editingDowncast').add((dispatcher) =>
      dispatcher.on('attribute', (evt, data, conversionApi) => {
        if (!_.includes([DATA_ASSET_SELECTION_ID, DATA_ASSET_SELECTION_DEPTH_LEVEL], data.attributeKey)) return;
        const modelElement: ModelElement = data.item as ModelElement;
        conversionApi.consumable.consume(data.item, evt.name);
        const viewElement = conversionApi.mapper.toViewElement(modelElement);

        const domElement = this.editor.editing.view.domConverter.mapViewToDom(viewElement.getChild(0));
        if (!domElement) return;
        this.renderElement(modelElement, domElement);
      }),
    );
  }

  createDataElement(modelItem, viewWriter) {
    const placeholderView = viewWriter.createContainerElement('span', {
      [DATA_ASSET_SELECTION_ID]: modelItem.getAttribute(DATA_ASSET_SELECTION_ID) || '',
      [DATA_ASSET_SELECTION_DEPTH_LEVEL]: modelItem.getAttribute(DATA_ASSET_SELECTION_DEPTH_LEVEL) || '',
      class: CLASSES,
    });
    return placeholderView;
  }

  createViewElement(modelItem, viewWriter) {
    const placeholderView = viewWriter.createContainerElement('span', {
      class: CLASSES,
    });
    // This element will host a React <Content /> component.
    const reactWrapper = viewWriter.createRawElement(
      'span',
      {
        class: 'reactWrapper',
      },
      (domElement) => {
        this.renderElement(modelItem, domElement);
      },
    );

    viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), reactWrapper);

    return placeholderView;
  }

  renderElement(modelItem, domElement) {
    const editorConfiguration = this.editor.config;
    const deps: BasePluginDependencies = editorConfiguration.get(PluginDependencies.pluginName);
    const assetSelectionId = modelItem.getAttribute(DATA_ASSET_SELECTION_ID);
    const assetSelectionDepthLevel = modelItem.getAttribute(DATA_ASSET_SELECTION_DEPTH_LEVEL);

    elementWithRoot(
      deps,
      domElement,
      <EditAssetSelectionLabel
        viewMode={!deps.canModify}
        updateId={this.updateAttribute(modelItem, DATA_ASSET_SELECTION_ID)}
        updateDepthLevel={this.updateAttribute(modelItem, DATA_ASSET_SELECTION_DEPTH_LEVEL)}
        pathLevel={assetSelectionDepthLevel}
        assetSelectionId={assetSelectionId}
      />,
    );
    this.reactDomElements.push(domElement);
  }

  updateAttribute(modelItem, attribute) {
    return (value) =>
      this.editor.model.change((writer) => {
        writer.setAttribute(attribute, value, modelItem);
      });
  }
}

export class AssetLabelCommand extends Command {
  execute() {
    const editor = this.editor;
    editor.model.change((writer) => {
      if (editor.model.document.selection.getSelectedElement()) {
        writer.setSelection(editor.model.document.selection.getSelectedElement(), 'after');
      }
      // Create a <placeholder> elment with the "name" attribute (and all the selection attributes)...
      const element = writer.createElement(SCHEMA_MODEL_NAME, []);

      // ... and insert it into the document.
      editor.model.insertObject(element);

      // Put the selection on the inserted element.
      writer.setSelection(element, 'on');
      // we need to focus back on the editor after a short delay
      editor.editing.view.focus();
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;

    const isAllowed = model.schema.checkChild(selection.focus.parent, SCHEMA_MODEL_NAME);
    this.isEnabled = isAllowed;
  }
}
